import { useEffect, useState } from "react";
import QRCode from "qrcode";
import type { SlideProps } from "@/engine/app/offsite-config";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { api } from "@/api/client";
import { useStore } from "@/state/store";

/** SVO-Farben für die Wolke — rotierend, gewichtete Begriffe größer. */
const WOLKE_FARBEN = ["#008DF7", "#79D97C", "#FFFFFF", "#F5A623"];

/**
 * Live-Wortwolke aus der offenen Runde: Die Diskussions-Notizen (Folie 27)
 * bündelt die KI zu Begriffen (GET /admin/ideen-aggregat, serverseitig
 * gecacht, neu nur bei neuen Notizen). Die Danke-Folie zeigt so am Ende,
 * was den Raum bewegt hat — ohne zweite Erfassung.
 */
function NotizenWolke() {
  const { state } = useStore();
  const token = state.auth.token;
  const istPresenter = state.auth.role === "presenter";
  const [begriffe, setBegriffe] = useState<{ text: string; gewicht: number }[]>([]);

  useEffect(() => {
    if (!istPresenter) return;
    let aktiv = true;
    const laden = async () => {
      try {
        const r = await api.admin.ideenAggregat(token);
        if (aktiv && r.begriffe.length > 0) setBegriffe(r.begriffe);
      } catch {
        /* ohne Wolke bleibt die Folie einfach die Danke-Folie */
      }
    };
    void laden();
    const id = setInterval(() => void laden(), 15000);
    return () => {
      aktiv = false;
      clearInterval(id);
    };
  }, [token, istPresenter]);

  if (begriffe.length === 0) return null;
  const maxGewicht = Math.max(1, ...begriffe.map((b) => b.gewicht));

  return (
    <div
      className="rise rise-5"
      aria-hidden="true"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px 20px",
        alignItems: "baseline",
        justifyContent: "center",
        maxWidth: 1100,
        margin: "0 auto",
      }}
    >
      {begriffe.map((b, i) => (
        <span
          key={b.text}
          style={{
            // Etwas kleiner als ohne QR: Wolke, Name und Ergebnis-QR teilen
            // sich die Folie, sonst rutscht die Fußzeile unter die Leiste.
            fontSize: `clamp(.9rem, ${0.9 + (b.gewicht / maxGewicht) * 1.9}vw, ${1.1 + (b.gewicht / maxGewicht) * 1.8}rem)`,
            fontWeight: b.gewicht >= maxGewicht * 0.7 ? 700 : 500,
            color: WOLKE_FARBEN[i % WOLKE_FARBEN.length],
            opacity: 0.55 + 0.45 * (b.gewicht / maxGewicht),
            lineHeight: 1.1,
          }}
        >
          {b.text}
        </span>
      ))}
    </div>
  );
}

/**
 * QR zur Ergebnisseite (offsiteConfig.ergebnisse). Der Pfad ist schon vor dem
 * Offsite reserviert — bis zum Bau der echten Seite liegt dort ein
 * Platzhalter, der Code auf der Leinwand bleibt also gültig.
 */
function ErgebnisseQr() {
  const { ergebnisse } = useOffsiteConfig();
  const [qrSvg, setQrSvg] = useState<string | null>(null);

  useEffect(() => {
    if (!ergebnisse) return;
    QRCode.toString(ergebnisse.url, {
      type: "svg",
      // "M" statt "H": die URL ist lang, und jedes Korrekturniveau mehr
      // verdichtet das Muster weiter — vom Beamer wird es sonst unlesbar.
      errorCorrectionLevel: "M",
      margin: 1,
      color: { dark: "#001E62", light: "#FFFFFF" },
    })
      .then(setQrSvg)
      .catch(() => setQrSvg(null));
  }, [ergebnisse]);

  if (!ergebnisse) return null;
  return (
    <div className="rise rise-5" style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
      <div
        aria-hidden="true"
        style={{
          width: 180,
          height: 180,
          background: "#fff",
          borderRadius: 12,
          padding: 10,
          display: "grid",
          placeItems: "center",
        }}
      >
        {qrSvg ? (
          <span
            style={{ display: "block", width: "100%", height: "100%" }}
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
        ) : null}
      </div>
      <div style={{ maxWidth: "15rem" }}>
        <div
          style={{
            fontSize: ".72rem",
            letterSpacing: ".12em",
            textTransform: "uppercase",
            fontWeight: 600,
            color: "var(--svo-blau)",
            marginBottom: 6,
          }}
        >
          Alles zum Nachlesen
        </div>
        <div style={{ color: "var(--svo-weiss)", fontSize: "1rem", lineHeight: 1.35, fontWeight: 500 }}>
          Eure Prototypen, die Bewertungen und diese Folien — ab heute Nachmittag
          hinter diesem Code.
        </div>
      </div>
    </div>
  );
}

export function Slide19Danke(_: SlideProps) {
  return (
    <div className="deck-root dark slide-manifesto" style={{ background: "var(--svo-dunkelblau)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <div
        className="float"
        aria-hidden="true"
        style={{
          width: 420,
          height: 420,
          top: "-120px",
          right: "-100px",
          background: "var(--svo-blau)",
          opacity: 0.3,
        }}
      />
      <div
        className="float"
        aria-hidden="true"
        style={{
          width: 320,
          height: 320,
          bottom: "-100px",
          left: "10%",
          background: "var(--svo-gruen)",
          opacity: 0.12,
          animationDelay: "4s",
        }}
      />

      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1">Danke</div>
        <h1
          className="deck-title rise rise-2"
          style={{
            fontSize: "clamp(4rem, 9vw, 7.5rem)",
            fontWeight: 200,
            color: "var(--svo-blau)",
            lineHeight: 0.95,
            letterSpacing: "-0.04em",
          }}
        >
          Danke!
        </h1>
        <p className="deck-sub rise rise-3" style={{ fontStyle: "italic" }}>
          Wir gestalten die SVO bis 2030 durchgehend digital — und das klappt
          nur mit eurer Hilfe.
        </p>
      </header>

      {/* Was den Raum bewegt hat — KI-gebündelt aus der offenen Runde. */}
      <NotizenWolke />

      <div
        className="deck-lead"
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: "2rem",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="lead-body rise rise-4" style={{ color: "var(--svo-weiss)", fontWeight: 500 }}>
            Viktor Lüders
          </div>
          <div className="lead-body rise rise-5" style={{ color: "rgba(255,255,255,0.7)" }}>
            IT Requirements Manager · SVO
          </div>
        </div>
        <ErgebnisseQr />
      </div>

      <footer className="deck-foot">
        <span>SVO · Celle · Uelzen</span>
      </footer>
    </div>
  );
}
